export const state = () => ({
  patientForm: {
    // Personal Information
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    flagHighRisk: false,
    assignedRisk: "",

    // Medical Information (will store patient assignment IDs and data)
    chronicConditions: [],
    allergies: [],
    medications: [],

    // Caregiver Information
    caregiverName: "",
    caregiverPhone: "",

    // Contact Information
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    email: "",
  },

  // Search results for autocomplete
  searchResults: {
    chronicConditions: [],
    allergies: [],
    medications: [],
  },

  // Patient assignments (detailed data from backend)
  patientAssignments: {
    chronicConditions: [],
    allergies: [],
    medications: [],
  },

  // Loading states
  loading: {
    searchChronicConditions: false,
    searchAllergies: false,
    searchMedications: false,
    patientData: false,
    saving: false,
  },

  // Search states
  searchQueries: {
    chronicConditions: "",
    allergies: "",
    medications: "",
  },

  // Validation state
  isFormValid: false,
  hasBeenSubmitted: false,

  // Current patient and organization IDs
  currentPatientId: null,
  currentOrganizationId: null,

  // Original risk level for comparison
  originalRiskLevel: null,

  // Care packages state
  carePackages: [],
  currentCarePackage: null,
  carePackageLoading: false,
  carePackageError: null,

  // Care package risk level
  carePackageRiskLevel: null,
});

export const mutations = {
  updatePatientForm(state, formData) {
    state.patientForm = { ...state.patientForm, ...formData };
  },

  setPatientFormField(state, { field, value }) {
    state.patientForm[field] = value;
  },

  setCurrentIds(state, { patientId, organizationId }) {
    console.log("setCurrentIds mutation called with:", {
      patientId,
      organizationId,
    });
    state.currentPatientId = patientId;
    state.currentOrganizationId = organizationId;
    console.log("Store state after setCurrentIds:", {
      currentPatientId: state.currentPatientId,
      currentOrganizationId: state.currentOrganizationId,
    });
  },

  // Search results mutations
  setSearchResults(state, { type, data }) {
    state.searchResults[type] = data;
  },

  setSearchQuery(state, { type, query }) {
    state.searchQueries[type] = query;
  },

  clearSearchResults(state, type) {
    state.searchResults[type] = [];
  },

  // Patient assignments mutations
  setPatientAssignments(state, { type, data }) {
    state.patientAssignments[type] = data;
    // Update form data with objects containing assignment ID and name for display
    state.patientForm[type] = data.map((item) => {
      switch (type) {
        case "chronicConditions":
          return {
            id: item.id, // Patient assignment ID for deletion
            name: item.chronic_condition?.name || item.name,
          };
        case "allergies":
          return {
            id: item.id, // Patient assignment ID for deletion
            name: item.allergy?.name || item.name,
          };
        case "medications":
          return {
            id: item.id, // Patient assignment ID for deletion
            name: item.medication?.name || item.name,
          };
        default:
          return {
            id: item.id, // Patient assignment ID for deletion
            name: item.name,
          };
      }
    });
  },

  addPatientAssignment(state, { type, assignment }) {
    state.patientAssignments[type].push(assignment);
    // Update form data with object containing id and name
    let itemData;
    switch (type) {
      case "chronicConditions":
        itemData = {
          id: assignment.id, // Patient assignment ID for deletion
          name: assignment.chronic_condition?.name || assignment.name,
        };
        break;
      case "allergies":
        itemData = {
          id: assignment.id, // Patient assignment ID for deletion
          name: assignment.allergy?.name || assignment.name,
        };
        break;
      case "medications":
        itemData = {
          id: assignment.id, // Patient assignment ID for deletion
          name: assignment.medication?.name || assignment.name,
        };
        break;
      default:
        itemData = {
          id: assignment.id, // Patient assignment ID for deletion
          name: assignment.name,
        };
    }

    // Check if item already exists by id
    const existingIndex = state.patientForm[type].findIndex(
      (item) => item.id === itemData.id
    );
    if (existingIndex === -1) {
      state.patientForm[type].push(itemData);
    }
  },

  removePatientAssignment(state, { type, itemId }) {
    // Find and remove from patientAssignments array
    const assignmentIndex = state.patientAssignments[type].findIndex(
      (item) => item.id === itemId
    );
    if (assignmentIndex !== -1) {
      state.patientAssignments[type].splice(assignmentIndex, 1);
    }

    // Find and remove from patientForm array
    const formIndex = state.patientForm[type].findIndex(
      (item) => item.id === itemId
    );
    if (formIndex !== -1) {
      state.patientForm[type].splice(formIndex, 1);
    }
  },

  // Loading state mutations
  setLoading(state, { type, value }) {
    state.loading[type] = value;
  },

  setFormValidation(state, isValid) {
    state.isFormValid = isValid;
  },

  setSubmissionState(state, hasBeenSubmitted) {
    state.hasBeenSubmitted = hasBeenSubmitted;
  },

  setOriginalRiskLevel(state, riskLevel) {
    state.originalRiskLevel = riskLevel;
  },

  // Reset form
  resetPatientForm(state) {
    state.patientForm = {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      flagHighRisk: false,
      assignedRisk: "",
      chronicConditions: [],
      allergies: [],
      medications: [],
      caregiverName: "",
      caregiverPhone: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      phoneNumber: "",
      email: "",
    };
    state.patientAssignments = {
      chronicConditions: [],
      allergies: [],
      medications: [],
    };
    state.searchResults = {
      chronicConditions: [],
      allergies: [],
      medications: [],
    };
    state.isFormValid = false;
    state.hasBeenSubmitted = false;
  },

  // Care Package Mutations
  setCarePackages(state, carePackages) {
    state.carePackages = carePackages;
  },
  setCurrentCarePackage(state, carePackage) {
    state.currentCarePackage = carePackage;
  },
  addCarePackage(state, carePackage) {
    state.carePackages.push(carePackage);
  },
  updateCarePackage(state, updatedPackage) {
    const index = state.carePackages.findIndex(
      (pkg) => pkg.id === updatedPackage.id
    );
    if (index !== -1) {
      state.carePackages.splice(index, 1, updatedPackage);
    }
    if (state.currentCarePackage?.id === updatedPackage.id) {
      state.currentCarePackage = updatedPackage;
    }
  },
  removeCarePackage(state, packageId) {
    state.carePackages = state.carePackages.filter(
      (pkg) => pkg.id !== packageId
    );
    if (state.currentCarePackage?.id === packageId) {
      state.currentCarePackage = null;
    }
  },
  setCarePackageLoading(state, loading) {
    state.carePackageLoading = loading;
  },
  setCarePackageError(state, error) {
    state.carePackageError = error;
  },
  clearCarePackageError(state) {
    state.carePackageError = null;
  },

  // Set risk level from care package data
  setCarePackageRiskLevel(state, riskLevel) {
    state.carePackageRiskLevel = riskLevel;
    // Also update the patient form if we have a risk level
    if (riskLevel) {
      // Map API values to UI values
      let formattedRiskLevel;
      if (riskLevel.toLowerCase() === "medium") {
        formattedRiskLevel = "Moderate";
      } else {
        formattedRiskLevel =
          riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1);
      }

      state.patientForm.assignedRisk = formattedRiskLevel;
      // Set as original risk level if it hasn't been set yet
      if (!state.originalRiskLevel) {
        state.originalRiskLevel = formattedRiskLevel;
      }
    }
  },
};

export const actions = {
  // Initialize store with patient and organization IDs
  async initializeStore(
    { commit, dispatch, state },
    { patientId, organizationId }
  ) {
    console.log("initializeStore called with:", { patientId, organizationId });

    // Ensure we have organization ID from localStorage if not provided
    const finalOrganizationId =
      organizationId || localStorage.getItem("organizationId");

    commit("setCurrentIds", {
      patientId: patientId || null,
      organizationId: finalOrganizationId,
    });

    if (patientId) {
      await dispatch("loadPatientMedicalData");
    }
  },

  // Search functions with debounce
  async searchChronicConditions({ commit, state }, { query, debounce = true }) {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId || !query || query.length < 2) {
      commit("clearSearchResults", "chronicConditions");
      return [];
    }

    commit("setSearchQuery", { type: "chronicConditions", query });
    commit("setLoading", { type: "searchChronicConditions", value: true });

    try {
      const response = await this.$axios.$get(
        `${organizationId}/medrina/care-plan-chronic-conditions`,
        {
          params: {
            search: query,
            active: true,
            per_page: 10, // Limit results for autocomplete
          },
        }
      );

      commit("setSearchResults", {
        type: "chronicConditions",
        data: response.data,
      });
      return response.data;
    } catch (error) {
      console.error("Error searching chronic conditions:", error);
      commit("clearSearchResults", "chronicConditions");
      return [];
    } finally {
      commit("setLoading", { type: "searchChronicConditions", value: false });
    }
  },

  async searchAllergies({ commit, state }, { query, debounce = true }) {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId || !query || query.length < 2) {
      commit("clearSearchResults", "allergies");
      return [];
    }

    commit("setSearchQuery", { type: "allergies", query });
    commit("setLoading", { type: "searchAllergies", value: true });

    try {
      const response = await this.$axios.$get(
        `${organizationId}/medrina/care-plan-allergies`,
        {
          params: {
            search: query,
            active: true,
            per_page: 10,
          },
        }
      );

      commit("setSearchResults", { type: "allergies", data: response.data });
      return response.data;
    } catch (error) {
      console.error("Error searching allergies:", error);
      commit("clearSearchResults", "allergies");
      return [];
    } finally {
      commit("setLoading", { type: "searchAllergies", value: false });
    }
  },

  async searchMedications({ commit, state }, { query, debounce = true }) {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId || !query || query.length < 2) {
      commit("clearSearchResults", "medications");
      return [];
    }

    commit("setSearchQuery", { type: "medications", query });
    commit("setLoading", { type: "searchMedications", value: true });

    try {
      const response = await this.$axios.$get(
        `${organizationId}/medrina/care-plan-medications`,
        {
          params: {
            search: query,
            active: true,
            per_page: 10,
          },
        }
      );

      commit("setSearchResults", { type: "medications", data: response.data });
      return response.data;
    } catch (error) {
      console.error("Error searching medications:", error);
      commit("clearSearchResults", "medications");
      return [];
    } finally {
      commit("setLoading", { type: "searchMedications", value: false });
    }
  },

  // Load basic patient information (firstName, lastName, etc.)
  async loadPatientBasicInfo({ commit, state }) {
    if (!state.currentPatientId) {
      return;
    }

    commit("setLoading", { type: "patientData", value: true });

    try {
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");

      if (!organizationId) {
        throw new Error("Organization ID not found in localStorage");
      }

      // Load basic patient information from the patients endpoint
      const patientResponse = await this.$axios.$get(
        `${organizationId}/patients/${patientId}`
      );

      const patientData = patientResponse.data;

      if (patientData) {
        // Map API values to UI values and format risk level
        let riskLevel = "";
        if (patientData.risk_level) {
          if (patientData.risk_level.toLowerCase() === "medium") {
            riskLevel = "Moderate";
          } else {
            riskLevel =
              patientData.risk_level.charAt(0).toUpperCase() +
              patientData.risk_level.slice(1);
          }
        }

        commit("updatePatientForm", {
          firstName: patientData.first_name || "",
          lastName: patientData.last_name || "",
          gender: patientData.gender || "",
          dateOfBirth: patientData.date_of_birth || "",
          assignedRisk: riskLevel,
          address: patientData.address_line_1 || "",
          city: patientData.city || "",
          state: patientData.state || "",
          zipCode: patientData.zip_code || "",
          phoneNumber: patientData.phone_number || "",
          email: patientData.email || "",
        });

        // Set original risk level for change detection
        commit("setOriginalRiskLevel", riskLevel);
      }
    } catch (error) {
      console.error("Error loading basic patient info:", error);
    } finally {
      commit("setLoading", { type: "patientData", value: false });
    }
  },

  // Load patient medical data from existing assignments
  async loadPatientMedicalData({ commit, state }) {
    if (!state.currentPatientId) {
      console.log("No currentPatientId, skipping loadPatientMedicalData");
      return;
    }

    console.log("Loading patient medical data for:", state.currentPatientId);
    commit("setLoading", { type: "patientData", value: true });

    try {
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");

      if (!organizationId) {
        throw new Error("Organization ID not found in localStorage");
      }

      console.log("Fetching patient assignments from:", {
        allergies: `${organizationId}/patients/${patientId}/allergies`,
        medications: `${organizationId}/patients/${patientId}/patient-medications`,
        chronicConditions: `${organizationId}/patients/${patientId}/chronic-conditions`,
      });

      // Load all patient assignments in parallel
      const [allergiesResponse, medicationsResponse, conditionsResponse] =
        await Promise.all([
          this.$axios.$get(
            `${organizationId}/medrina/patients/${patientId}/allergies`
          ),
          this.$axios.$get(
            `${organizationId}/medrina/patients/${patientId}/patient-medications`
          ),
          this.$axios.$get(
            `${organizationId}/medrina/patients/${patientId}/chronic-conditions`
          ),
        ]);

      console.log("Patient medical data responses:", {
        allergies: allergiesResponse,
        medications: medicationsResponse,
        chronicConditions: conditionsResponse,
      });

      // Set patient assignments with the fetched data
      // Note: Using $axios returns the response data directly, and the actual array is under .data property
      commit("setPatientAssignments", {
        type: "allergies",
        data: allergiesResponse.data || [],
      });
      commit("setPatientAssignments", {
        type: "medications",
        data: medicationsResponse.data || [],
      });
      commit("setPatientAssignments", {
        type: "chronicConditions",
        data: conditionsResponse.data || [],
      });

      console.log("Patient assignments loaded successfully");
    } catch (error) {
      console.error("Error loading patient medical data:", error);
      // Initialize with empty arrays on error
      commit("setPatientAssignments", { type: "allergies", data: [] });
      commit("setPatientAssignments", { type: "medications", data: [] });
      commit("setPatientAssignments", { type: "chronicConditions", data: [] });
    } finally {
      commit("setLoading", { type: "patientData", value: false });
    }
  },

  // Create new master list item
  async createMasterListItem({ dispatch, state }, { type, itemData }) {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return null;

    try {
      const endpoint = `${organizationId}/medrina/care-plan-${type}`;

      const response = await this.$axios.$post(endpoint, itemData);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      throw error;
    }
  },

  // Patient assignment actions
  async assignAllergyToPatient(
    { commit, state },
    { allergyId, additionalData = {} }
  ) {
    console.log(
      "assignAllergyToPatient",
      "currentPatientId:",
      state.currentPatientId,
      "allergyId:",
      allergyId
    );

    if (!state.currentPatientId) {
      console.error(
        "Cannot assign allergy: Patient ID is required but currentPatientId is null"
      );
      throw new Error("Patient ID is required to assign allergy");
    }

    try {
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");
      const response = await this.$axios.$post(
        `${organizationId}/medrina/patients/${patientId}/allergies`,
        {
          care_plan_allergy_id: allergyId,
          active: true,
          ...additionalData,
        }
      );

      // Pass the full assignment object, not just the allergy part
      commit("addPatientAssignment", {
        type: "allergies",
        assignment: response.data,
      });
      return response.data;
    } catch (error) {
      console.error("Error assigning allergy:", error);
      throw error;
    }
  },

  async assignMedicationToPatient(
    { commit, state },
    { medicationId, additionalData = {} }
  ) {
    console.log(
      "assignMedicationToPatient",
      "currentPatientId:",
      state.currentPatientId,
      "medicationId:",
      medicationId
    );

    if (!state.currentPatientId) {
      console.error(
        "Cannot assign medication: Patient ID is required but currentPatientId is null"
      );
      throw new Error("Patient ID is required to assign medication");
    }

    try {
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");
      const response = await this.$axios.$post(
        `${organizationId}/medrina/patients/${patientId}/patient-medications`,
        {
          care_plan_medication_id: medicationId,
          active: true,
          ...additionalData,
        }
      );

      // Pass the full assignment object, not just the medication part
      commit("addPatientAssignment", {
        type: "medications",
        assignment: response.data,
      });
      return response.data;
    } catch (error) {
      console.error("Error assigning medication:", error);
      throw error;
    }
  },

  async assignChronicConditionToPatient(
    { commit, state },
    { conditionId, additionalData = {} }
  ) {
    console.log(
      "assignChronicConditionToPatient action",
      "currentPatientId:",
      state.currentPatientId,
      "conditionId:",
      conditionId,
      "Full state:",
      {
        currentPatientId: state.currentPatientId,
        currentOrganizationId: state.currentOrganizationId,
      }
    );

    if (!state.currentPatientId) {
      console.error(
        "Cannot assign condition: Patient ID is required but currentPatientId is null",
        "Full store state:",
        state
      );
      throw new Error("Patient ID is required to assign chronic condition");
    }

    try {
      console.log("assignChronicConditionToPatient", conditionId);
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");
      const response = await this.$axios.$post(
        `${organizationId}/medrina/patients/${patientId}/chronic-conditions`,
        {
          care_plan_chronic_condition_id: conditionId,
          active: true,
          ...additionalData,
        }
      );

      // Pass the full assignment object, not just the chronic condition part
      commit("addPatientAssignment", {
        type: "chronicConditions",
        assignment: response.data,
      });
      return response.data;
    } catch (error) {
      console.error("Error assigning chronic condition:", error);
      throw error;
    }
  },

  // Remove patient assignments
  async removeAllergyFromPatient({ commit, state }, item) {
    if (!state.currentPatientId) return;

    try {
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");

      await this.$axios.$delete(
        `${organizationId}/medrina/patients/${patientId}/allergies/${item.id}`
      );

      // Use mutation to remove from state
      commit("removePatientAssignment", {
        type: "allergies",
        itemId: item.id,
      });
    } catch (error) {
      console.error("Error removing allergy:", error);
      throw error;
    }
  },

  async removeMedicationFromPatient({ commit, state }, item) {
    if (!state.currentPatientId) return;

    try {
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");

      await this.$axios.$delete(
        `${organizationId}/medrina/patients/${patientId}/patient-medications/${item.id}`
      );

      // Use mutation to remove from state
      commit("removePatientAssignment", {
        type: "medications",
        itemId: item.id,
      });
    } catch (error) {
      console.error("Error removing medication:", error);
      throw error;
    }
  },

  async removeChronicConditionFromPatient({ commit, state }, item) {
    if (!state.currentPatientId) return;

    try {
      const patientId = state.currentPatientId;
      const organizationId = localStorage.getItem("organizationId");

      await this.$axios.$delete(
        `${organizationId}/medrina/patients/${patientId}/chronic-conditions/${item.id}`
      );

      // Use mutation to remove from state
      commit("removePatientAssignment", {
        type: "chronicConditions",
        itemId: item.id,
      });
    } catch (error) {
      console.error("Error removing chronic condition:", error);
      throw error;
    }
  },

  // Add items by search and create if needed
  async addChronicConditionBySearch({ dispatch, state }, conditionName) {
    console.log("addChronicConditionBySearch", conditionName);
    // First try to search for existing condition
    const results = await dispatch("searchChronicConditions", {
      query: conditionName,
    });

    let condition = results.find(
      (c) => c.name.toLowerCase() === conditionName.toLowerCase()
    );

    if (!condition) {
      // Create new condition if not found
      condition = await dispatch("createMasterListItem", {
        type: "chronic-conditions",
        itemData: { name: conditionName },
      });
    }

    if (condition) {
      console.log("assignChronicConditionToPatient dispatch", condition);
      return await dispatch("assignChronicConditionToPatient", {
        conditionId: condition.id,
      });
    }
  },

  async addAllergyBySearch({ dispatch, state }, allergyName) {
    const results = await dispatch("searchAllergies", { query: allergyName });

    let allergy = results.find(
      (a) => a.name.toLowerCase() === allergyName.toLowerCase()
    );

    if (!allergy) {
      allergy = await dispatch("createMasterListItem", {
        type: "allergies",
        itemData: { name: allergyName },
      });
    }

    if (allergy) {
      return await dispatch("assignAllergyToPatient", {
        allergyId: allergy.id,
      });
    }
  },

  async addMedicationBySearch({ dispatch, state }, medicationName) {
    const results = await dispatch("searchMedications", {
      query: medicationName,
    });

    let medication = results.find(
      (m) => m.name.toLowerCase() === medicationName.toLowerCase()
    );

    if (!medication) {
      medication = await dispatch("createMasterListItem", {
        type: "medications",
        itemData: { name: medicationName },
      });
    }

    if (medication) {
      return await dispatch("assignMedicationToPatient", {
        medicationId: medication.id,
      });
    }
  },

  // Update patient risk level
  async updatePatientRiskLevel({ commit, state }, { riskLevel }) {
    const organizationId = localStorage.getItem("organizationId");
    const patientId = state.currentPatientId;

    if (!organizationId || !patientId) {
      throw new Error("Organization ID and Patient ID are required");
    }

    commit("setLoading", { type: "savingRiskLevel", value: true });

    try {
      // Map UI values to API values
      const apiRiskLevel =
        riskLevel === "Moderate" ? "medium" : riskLevel.toLowerCase();

      const response = await this.$axios.$put(
        `${organizationId}/medrina/patients/${patientId}/risk-level`,
        {
          risk_level: apiRiskLevel,
        }
      );

      // Update the care package risk level and original risk level to match what was saved
      commit("setCarePackageRiskLevel", apiRiskLevel);
      commit("setOriginalRiskLevel", riskLevel); // Keep UI value in store

      return response;
    } catch (error) {
      console.error("Error updating patient risk level:", error);
      throw error;
    } finally {
      commit("setLoading", { type: "savingRiskLevel", value: false });
    }
  },

  // Legacy actions for component compatibility
  updateForm({ commit, dispatch }, formData) {
    commit("updatePatientForm", formData);
    dispatch("validateForm");
  },

  updateField({ commit, dispatch }, { field, value }) {
    commit("setPatientFormField", { field, value });
    dispatch("validateForm");
  },

  validateForm({ commit, state }) {
    const form = state.patientForm;
    const isValid = !!(form.firstName && form.lastName && form.dateOfBirth);
    commit("setFormValidation", isValid);
  },

  // Medical information actions - handle both objects (from search results) and strings (new items)
  async addChronicCondition({ commit, state, dispatch }, condition) {
    console.log("addChronicCondition", condition, typeof condition);

    if (typeof condition === "string") {
      // String input - search and create if needed, then assign
      return await dispatch("addChronicConditionBySearch", condition);
    } else if (condition && condition.id) {
      // Object with id - assign directly from search results
      return await dispatch("assignChronicConditionToPatient", {
        conditionId: condition.id,
      });
    }
  },

  async removeChronicCondition({ dispatch }, item) {
    return await dispatch("removeChronicConditionFromPatient", item);
  },

  async addAllergy({ commit, state, dispatch }, allergy) {
    console.log("addAllergy", allergy, typeof allergy);

    if (typeof allergy === "string") {
      // String input - search and create if needed, then assign
      return await dispatch("addAllergyBySearch", allergy);
    } else if (allergy && allergy.id) {
      // Object with id - assign directly from search results
      return await dispatch("assignAllergyToPatient", {
        allergyId: allergy.id,
      });
    }
  },

  async removeAllergy({ dispatch }, item) {
    return await dispatch("removeAllergyFromPatient", item);
  },

  async addMedication({ commit, state, dispatch }, medication) {
    console.log("addMedication", medication, typeof medication);

    if (typeof medication === "string") {
      // String input - search and create if needed, then assign
      return await dispatch("addMedicationBySearch", medication);
    } else if (medication && medication.id) {
      // Object with id - assign directly from search results
      return await dispatch("assignMedicationToPatient", {
        medicationId: medication.id,
      });
    }
  },

  async removeMedication({ dispatch }, item) {
    return await dispatch("removeMedicationFromPatient", item);
  },

  // Save patient form
  async savePatientForm({ state, commit }) {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    commit("setLoading", { type: "saving", value: true });

    try {
      const response = await this.$axios.$post(
        `${organizationId}/medrina/care-plans/patients`,
        state.patientForm
      );
      return response;
    } catch (error) {
      console.error("Error saving patient form:", error);
      throw error;
    } finally {
      commit("setLoading", { type: "saving", value: false });
    }
  },

  resetForm({ commit }) {
    commit("resetPatientForm");
  },

  // Care Package Actions
  async fetchCarePackages({ commit, state }, { organizationId, patientId }) {
    commit("setCarePackageLoading", true);
    commit("clearCarePackageError");

    try {
      const response = await this.$axios.$get(
        `/${organizationId}/medrina/patients/${patientId}/care-packages`
      );

      commit("setCarePackages", response || []);

      return response;
    } catch (error) {
      console.error("❌ Error fetching care packages:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch care packages";
      commit("setCarePackageError", errorMessage);
      commit("setCarePackages", []);
      throw error;
    } finally {
      commit("setCarePackageLoading", false);
    }
  },

  async ensureCarePackageExists(
    { commit, dispatch },
    { organizationId, patientId }
  ) {
    commit("setCarePackageLoading", true);
    commit("clearCarePackageError");

    try {
      console.log("🔍 Ensuring care package exists for patient:", patientId);

      // First check if care packages exist
      const response = await this.$axios.$get(
        `/${organizationId}/medrina/patients/${patientId}/care-packages`
      );

      if (response && response.length > 0) {
        console.log("✅ Care packages already exist for this patient");

        commit("setCarePackages", response);
        return response;
      }

      // No care packages exist, create one using the /latest endpoint
      console.log(
        "📦 No care packages found, creating one with latest endpoint..."
      );

      const createResponse = await this.$axios.$post(
        `/${organizationId}/medrina/patients/${patientId}/care-packages/latest`
      );

      console.log(
        "✅ Care package created successfully using /latest endpoint:",
        createResponse
      );

      // Fetch the updated list of care packages
      const updatedResponse = await this.$axios.$get(
        `/${organizationId}/medrina/patients/${patientId}/care-packages`
      );

      commit("setCarePackages", updatedResponse || []);
      return updatedResponse;
    } catch (error) {
      console.error("❌ Error ensuring care package exists:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to ensure care package exists";
      commit("setCarePackageError", errorMessage);
      throw error;
    } finally {
      commit("setCarePackageLoading", false);
    }
  },

  async fetchCarePackageDetails(
    { commit, state },
    { organizationId, patientId, packageId }
  ) {
    commit("setCarePackageLoading", true);
    commit("clearCarePackageError");

    try {
      const response = await this.$axios.$get(
        `/${organizationId}/medrina/patients/${patientId}/care-packages/${packageId}`
      );

      commit("setCurrentCarePackage", response);

      return response;
    } catch (error) {
      console.error("❌ Error fetching care package details:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch care package details";
      commit("setCarePackageError", errorMessage);
      throw error;
    } finally {
      commit("setCarePackageLoading", false);
    }
  },

  async createCarePackage(
    { commit, state },
    { organizationId, patientId, packageData }
  ) {
    commit("setCarePackageLoading", true);
    commit("clearCarePackageError");

    try {
      console.log("➕ Creating care package for patient:", patientId);

      const response = await this.$axios.$post(
        `/${organizationId}/medrina/patients/${patientId}/care-packages`,
        packageData
      );

      console.log("✅ Care package created:", response);
      commit("addCarePackage", response);

      return response;
    } catch (error) {
      console.error("❌ Error creating care package:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create care package";
      commit("setCarePackageError", errorMessage);
      throw error;
    } finally {
      commit("setCarePackageLoading", false);
    }
  },

  async updateCarePackage(
    { commit, state },
    { organizationId, patientId, packageId, packageData }
  ) {
    commit("setCarePackageLoading", true);
    commit("clearCarePackageError");

    try {
      console.log("🔄 Updating care package:", packageId);

      const response = await this.$axios.$put(
        `/${organizationId}/medrina/patients/${patientId}/care-packages/${packageId}`,
        packageData
      );

      console.log("✅ Care package updated:", response);
      commit("updateCarePackage", response);

      return response;
    } catch (error) {
      console.error("❌ Error updating care package:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update care package";
      commit("setCarePackageError", errorMessage);
      throw error;
    } finally {
      commit("setCarePackageLoading", false);
    }
  },

  async deleteCarePackage(
    { commit, state },
    { organizationId, patientId, packageId }
  ) {
    commit("setCarePackageLoading", true);
    commit("clearCarePackageError");

    try {
      console.log("🗑️ Deleting care package:", packageId);

      await this.$axios.$delete(
        `/${organizationId}/medrina/patients/${patientId}/care-packages/${packageId}`
      );

      console.log("✅ Care package deleted successfully");
      commit("removeCarePackage", packageId);

      return true;
    } catch (error) {
      console.error("❌ Error deleting care package:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete care package";
      commit("setCarePackageError", errorMessage);
      throw error;
    } finally {
      commit("setCarePackageLoading", false);
    }
  },

  async exportCarePackage(
    { commit, state },
    { organizationId, patientId, packageId, format = "pdf" }
  ) {
    commit("setCarePackageLoading", true);
    commit("clearCarePackageError");

    try {
      console.log("📤 Exporting care package:", packageId);

      const response = await this.$axios.get(
        `/${organizationId}/medrina/patients/${patientId}/care-packages/${packageId}/export`,
        {
          params: { format },
          responseType: "blob",
        }
      );

      console.log("✅ Care package exported successfully");
      return response.data;
    } catch (error) {
      console.error("❌ Error exporting care package:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to export care package";
      commit("setCarePackageError", errorMessage);
      throw error;
    } finally {
      commit("setCarePackageLoading", false);
    }
  },

  // Helper action to get care package data formatted for the CarePlanCard
  async getCarePackageForCard(
    { dispatch, commit, state },
    { organizationId, patientId }
  ) {
    try {
      const carePackages = await dispatch("fetchCarePackages", {
        organizationId,
        patientId,
      });

      if (carePackages && carePackages.length > 0) {
        // Get the first (most recent) care package details
        const firstPackage = carePackages[0];
        const packageId = firstPackage.id || firstPackage.package_id;

        const packageDetails = await dispatch("fetchCarePackageDetails", {
          organizationId,
          patientId,
          packageId,
        });

        return packageDetails;
      }

      return null;
    } catch (error) {
      console.error("❌ Error getting care package for card:", error);
      return null;
    }
  },

  clearCarePackageError({ commit }) {
    commit("clearCarePackageError");
  },

  clearCurrentCarePackage({ commit }) {
    commit("setCurrentCarePackage", null);
  },

  // Care Plan Review API Methods
  async updateCarePlanReview(
    { commit, state },
    { organizationId, patientId, carePlanId, reviewData }
  ) {
    try {
      const response = await this.$axios.$put(
        `${organizationId}/medrina/patients/${patientId}/care-packages/care-plans/${carePlanId}/summary`,
        reviewData
      );

      if (response.success) {
        console.log("Care plan review updated successfully:", response.data);
        return response;
      } else {
        throw new Error(
          response.message || "Failed to update care plan review"
        );
      }
    } catch (error) {
      console.error("Error updating care plan review:", error);
      throw error;
    }
  },

  async getCarePlanReviews(
    { commit, state },
    { organizationId, patientId, carePlanId, perPage = 15 }
  ) {
    try {
      const response = await this.$axios.$get(
        `${organizationId}/medrina/patients/${patientId}/care-packages/care-plans/${carePlanId}/reviews`,
        {
          params: { per_page: perPage },
        }
      );

      if (response.success) {
        console.log("Care plan reviews fetched successfully:", response.data);
        return response;
      } else {
        throw new Error(
          response.message || "Failed to fetch care plan reviews"
        );
      }
    } catch (error) {
      console.error("Error fetching care plan reviews:", error);
      throw error;
    }
  },

  async getCarePlanReviewByVersion(
    { commit, state },
    { organizationId, patientId, carePlanId, version }
  ) {
    try {
      const response = await this.$axios.$get(
        `${organizationId}/medrina/patients/${patientId}/care-packages/care-plans/${carePlanId}/reviews/${version}`
      );

      if (response.success) {
        console.log(
          "Care plan review version fetched successfully:",
          response.data
        );
        return response;
      } else {
        throw new Error(
          response.message || "Failed to fetch care plan review version"
        );
      }
    } catch (error) {
      console.error("Error fetching care plan review version:", error);
      throw error;
    }
  },

  async getCarePlanCurrentReview(
    { commit, state },
    { organizationId, patientId, carePlanId }
  ) {
    try {
      // Get all reviews and find the current one (is_current: true)
      const response = await this.$axios.$get(
        `${organizationId}/medrina/patients/${patientId}/care-packages/care-plans/${carePlanId}/reviews`
      );

      if (response.success && response.data && response.data.data) {
        // Find the current review
        const currentReview = response.data.data.find(
          (review) => review.is_current === true
        );

        if (currentReview) {
          console.log(
            "Care plan current review fetched successfully:",
            currentReview
          );
          return {
            success: true,
            data: currentReview,
          };
        } else {
          // No current review found
          return {
            success: true,
            data: null,
            message: "No current review found",
          };
        }
      } else {
        throw new Error(
          response.message || "Failed to fetch care plan reviews"
        );
      }
    } catch (error) {
      console.error("Error fetching current care plan review:", error);
      throw error;
    }
  },
};

export const getters = {
  patientForm(state) {
    return state.patientForm;
  },

  // Search results getters
  chronicConditionsSearchResults(state) {
    return state.searchResults.chronicConditions;
  },

  allergiesSearchResults(state) {
    return state.searchResults.allergies;
  },

  medicationsSearchResults(state) {
    return state.searchResults.medications;
  },

  // Legacy getters for backward compatibility (now return empty arrays since we use search)
  chronicConditionsData(state) {
    return state.searchResults.chronicConditions.map((item) => item.name);
  },

  allergiesData(state) {
    return state.searchResults.allergies.map((item) => item.name);
  },

  medicationsData(state) {
    return state.searchResults.medications.map((item) => item.name);
  },

  // Patient assignments (with full data)
  patientAssignments(state) {
    return state.patientAssignments;
  },

  // Loading states
  isLoading: (state) => (type) => {
    return state.loading[type] || false;
  },

  isFormValid(state) {
    return state.isFormValid;
  },

  hasBeenSubmitted(state) {
    return state.hasBeenSubmitted;
  },

  isFieldPrefilled: (state) => (fieldName) => {
    const value = state.patientForm[fieldName];
    return value && value.toString().trim() !== "";
  },

  currentPatientId(state) {
    return state.currentPatientId;
  },

  currentOrganizationId(state) {
    return state.currentOrganizationId;
  },

  originalRiskLevel(state) {
    return state.originalRiskLevel;
  },

  hasRiskLevelChanged(state) {
    return state.patientForm.assignedRisk !== state.originalRiskLevel;
  },

  // Care Package getters
  carePackages(state) {
    return state.carePackages;
  },
  carePackageRiskLevel(state) {
    return state.carePackageRiskLevel;
  },
  currentCarePackage(state) {
    return state.currentCarePackage;
  },
  carePackageById: (state) => (id) => {
    return state.carePackages.find((pkg) => pkg.id === id);
  },
  carePackageLoading(state) {
    return state.carePackageLoading;
  },
  carePackageError(state) {
    return state.carePackageError;
  },
  hasCarePackageError(state) {
    return !!state.carePackageError;
  },
  hasCarePackages(state) {
    return state.carePackages && state.carePackages.length > 0;
  },
};
